import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

interface MatchedContact {
  id: string;
  username: string;
  name: string;
  image_thumb: string | null;
  bio: string | null;
  isFollowing: boolean;
}

export function useContactMatching() {
  const [isMatching, setIsMatching] = useState(false);
  const [matches, setMatches] = useState<MatchedContact[]>([]);
  const [isSendingInvites, setIsSendingInvites] = useState(false);

  const hashEmail = async (email: string): Promise<string> => {
    const encoder = new TextEncoder();
    const data = encoder.encode(email.toLowerCase().trim());
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  };

  const matchContacts = async (emails: string[]) => {
    setIsMatching(true);
    try {
      // Hash all emails client-side
      const hashedEmails = await Promise.all(
        emails.map(email => hashEmail(email))
      );

      // Call edge function to match
      const { data, error } = await supabase.functions.invoke('match-contacts', {
        body: { hashedEmails },
      });

      if (error) throw error;

      setMatches(data.matches || []);
      
      if (data.matches && data.matches.length > 0) {
        toast({
          title: `Found ${data.matches.length} friends on Dino!`,
          description: 'You can now follow them to see their reading activity.',
        });
      } else {
        toast({
          title: 'No matches found',
          description: 'None of these contacts are on Dino yet. You can invite them!',
        });
      }

      return data.matches || [];
    } catch (error) {
      console.error('Error matching contacts:', error);
      toast({
        title: 'Failed to match contacts',
        description: 'Please try again.',
        variant: 'destructive',
      });
      return [];
    } finally {
      setIsMatching(false);
    }
  };

  const sendInvites = async (emails: string[]) => {
    setIsSendingInvites(true);
    try {
      const { data, error } = await supabase.functions.invoke('send-invite-email', {
        body: { emails },
      });

      if (error) throw error;

      toast({
        title: 'Invites sent!',
        description: `Successfully sent ${data.invitesSent} invitation${data.invitesSent === 1 ? '' : 's'}.`,
      });

      return true;
    } catch (error) {
      console.error('Error sending invites:', error);
      toast({
        title: 'Failed to send invites',
        description: 'Please try again.',
        variant: 'destructive',
      });
      return false;
    } finally {
      setIsSendingInvites(false);
    }
  };

  return {
    matchContacts,
    sendInvites,
    matches,
    isMatching,
    isSendingInvites,
    setMatches,
  };
}
